import { Link } from 'react-router-dom'
import { LockSimpleIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { intercomEnabled } from 'app/intercom'
import { gateMessage } from 'app/lib/workflows/blockGates'
import type { BlockedGate, GateLocation } from 'app/types/workflow-gates'

const SALES_CALENDLY_URL = `https://calendly.com/trackable/upgrade-to-crm`

type LockedGate = Extract<BlockedGate, { state: `locked` }>

/** Book with sales (Calendly, tagged with the product and block) and, where Intercom loads, a chat
 *  with the request already written. */
export function SalesActions({
  product,
  blockLabel,
  blockKind,
  location,
}: {
  product: string
  blockLabel: string
  blockKind: string
  location: GateLocation | undefined
}) {
  const book = new URL(SALES_CALENDLY_URL)
  book.searchParams.set(`utm_campaign`, product)
  book.searchParams.set(`utm_content`, `workflow-${blockKind}`)
  const chat = () =>
    window.Intercom?.(
      `showNewMessage`,
      `Hi! I'd like ${product} so I can use "${blockLabel}" in a workflow${location?.name ? ` at ${location.name}` : ``}.`,
    )
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" asChild data-testid="workflow-lock-book">
        <a href={book.toString()} target="_blank" rel="noreferrer">
          Book a time with sales
        </a>
      </Button>
      {intercomEnabled() && (
        <Button size="sm" variant="white" onClick={chat} data-testid="workflow-lock-chat">
          Chat with us
        </Button>
      )}
    </div>
  )
}

/** Why a block is padlocked at this location, and the way out: sales for a product, the
 *  integrations page for a practice system. Stands in for the upgrade path's locked pages. */
export function LockPanel({
  gate,
  blockLabel,
  blockKind,
  location,
}: {
  gate: LockedGate
  blockLabel: string
  blockKind: string
  location: GateLocation | undefined
}) {
  return (
    <div data-testid="workflow-lock-panel" className="space-y-3 text-sm">
      <p className="flex gap-2 text-default">
        <LockSimpleIcon size={16} weight="bold" className="mt-0.5 shrink-0 text-subtle" />
        {gateMessage(gate, blockLabel, location, blockKind)}
      </p>
      {gate.why === `product` ? (
        <SalesActions product={gate.product} blockLabel={blockLabel} blockKind={blockKind} location={location} />
      ) : (
        <Button size="sm" asChild data-testid="workflow-lock-connect">
          <Link to="/settings/integrations" target="_blank" rel="noreferrer">
            Connect
          </Link>
        </Button>
      )}
    </div>
  )
}
